import { useMemo, useRef, useState } from 'react'

import type { IServerSideDatasource, SortModelItem } from 'ag-grid-community'

import {
  attributeKeyToSortBy,
  buildManifestTableRows,
  useAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { getOrderProcessManifest } from '@b-stock/fe-core/bstock-next/queries'
import { ddLogger } from '@b-stock/fe-core/bstock-react'
import type {
  AvailableFiltersDto,
  GetManifestItemsByLotIdSortByEnum,
} from '@b-stock/order-process-api-client'
import { GetManifestItemsByLotIdSortOrderEnum } from '@b-stock/order-process-api-client'

const INLINE_MANIFEST_LIMIT = 12
export const FULLSCREEN_MANIFEST_LIMIT = 100
// [BP-2376] Larger block size for the inline grid once pagination is removed —
// 12-row blocks are too chatty under continuous infinite scroll.
export const INLINE_INFINITE_SCROLL_LIMIT = 100

export type ManifestFilters = Record<string, string[]>

export function useManifestDatasource({
  filters,
  limit = INLINE_MANIFEST_LIMIT,
  listing,
  search,
}: {
  filters: ManifestFilters
  limit?: number
  listing: OrchestratedListingDetails
  search: string
}): {
  availableFilters: AvailableFiltersDto[]
  datasource: IServerSideDatasource
  isLoading: boolean
  limit: number
  offset: number
  total: number
} {
  const accessToken = useAccessToken()
  const lotId = listing.lot._id
  const [availableFilters, setAvailableFilters] = useState<
    AvailableFiltersDto[]
  >([])
  const [isLoading, setIsLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  // Ref for getRows race condition — e.g. on header double-click.
  const latestRequestSeqRef = useRef(0)

  const datasource = useMemo<IServerSideDatasource>(
    () => ({
      getRows: (params) => {
        const seq = ++latestRequestSeqRef.current
        setIsLoading(true)
        void (async () => {
          const { startRow, sortModel } = params.request

          try {
            const result = await getOrderProcessManifest(accessToken, {
              exclude: ['metadata'],
              limit,
              lotId,
              offset: startRow ?? 0,
              ...(search && { search }),
              ...getFilterParams(filters),
              ...getSortParams(sortModel),
            })
            if (seq !== latestRequestSeqRef.current) return
            setAvailableFilters(result.availableFilters)
            setOffset(startRow ?? 0)
            setTotal(result.total)
            params.success({
              rowCount: result.total,
              rowData: buildManifestTableRows(result.items),
            })
          } catch (error) {
            ddLogger.error('failed to fetch manifest', { error, lotId })
            if (seq === latestRequestSeqRef.current) {
              params.fail()
            }
          } finally {
            if (seq === latestRequestSeqRef.current) {
              setIsLoading(false)
            }
          }
        })()
      },
    }),
    [accessToken, filters, limit, lotId, search]
  )

  return { availableFilters, datasource, isLoading, limit, offset, total }
}

const getFilterParams = (filters: ManifestFilters) =>
  Object.entries(filters).reduce<Record<string, string[]>>(
    (params, [key, values]) =>
      values.length > 0 ? { ...params, [key]: values } : params,
    {}
  )

const getSortParams = (
  sortModel: SortModelItem[]
): {
  sortBy?: GetManifestItemsByLotIdSortByEnum[]
  sortOrder?: GetManifestItemsByLotIdSortOrderEnum[]
} => {
  const items = sortModel
    .map(({ colId, sort }) => ({
      sortBy: attributeKeyToSortBy(`item.${colId}`),
      sortOrder:
        sort === 'asc'
          ? GetManifestItemsByLotIdSortOrderEnum.Asc
          : GetManifestItemsByLotIdSortOrderEnum.Desc,
    }))
    .filter((o) => !!o.sortBy)

  if (!items.length) return {}

  return {
    sortBy: items.map((o) => o.sortBy) as GetManifestItemsByLotIdSortByEnum[],
    sortOrder: items.map((o) => o.sortOrder),
  }
}
